<template>
  <div class="custom-data-table-wrapper">
    <div class="table-responsive">
      <table class="table table-striped table-hover table-sm mb-0">
        <thead>
          <tr>
            <th
              v-for="col in columns"
              :key="col.field || col.key"
              :width="col.width || 'auto'"
              scope="col"
              class="tablesorter-header"
              :class="[
                col.sortable !== false ? 'cursor-pointer' : '',
                orderClass(col.field || col.key),
                col.headerClass || '',
                col.align ? 'text-' + col.align : 'text-center'
              ]"
              @click="col.sortable !== false && onSort(col.field || col.key)"
            >
              <slot :name="'header-' + (col.field || col.key)" :col="col">
                {{ col.label }}
              </slot>
            </th>
            <th
              v-if="hasActions"
              scope="col"
              :width="actionsWidth"
              :class="actionsHeaderClass"
            >
              {{ actionsLabel }}
            </th>
          </tr>
        </thead>
        <tbody v-if="data && data.length">
          <slot name="body" :items="data">
            <tr
              v-for="(item, index) in data"
              :key="getItemKey(item, index)"
              :class="typeof rowClass === 'function' ? rowClass(item, index) : ''"
            >
              <slot name="row" :item="item" :index="index">
                <td
                  v-for="col in columns"
                  :key="col.field || col.key"
                  :class="[col.cellClass || '', col.align ? 'text-' + col.align : 'text-center']"
                >
                  <slot
                    :name="'cell-' + (col.field || col.key)"
                    :item="item"
                    :value="getFieldValue(item, col.field || col.key)"
                    :index="index"
                    :col="col"
                  >
                    <!-- Renderizado tipo badge automático para activo/enabled o col.type == 'badge' -->
                    <status-badge
                      v-if="col.type === 'badge' || (col.type !== 'text' && (col.field === 'activo' || col.field === 'enabled'))"
                      :value="getFieldValue(item, col.field || col.key)"
                      :active-text="col.activeText || 'Activo'"
                      :inactive-text="col.inactiveText || 'Inactivo'"
                    />

                    <!-- Formateador personalizado de columna -->
                    <span v-else-if="typeof col.formatter === 'function'">
                      {{ col.formatter(getFieldValue(item, col.field || col.key), item, index) }}
                    </span>

                    <!-- Renderizado de texto normal -->
                    <span v-else>
                      {{ getFieldValue(item, col.field || col.key) }}
                    </span>
                  </slot>
                </td>

                <!-- Columna de Acciones -->
                <td v-if="hasActions" class="text-center">
                  <slot name="actions" :item="item" :index="index"></slot>
                </td>
              </slot>
            </tr>
          </slot>
        </tbody>
      </table>
    </div>

    <!-- Estado vacío integrado -->
    <empty-state
      v-if="showEmptyState && (!data || !data.length)"
      :show="true"
      :message="emptyMessage"
    />
  </div>
</template>

<script>
import StatusBadge from "./StatusBadge.vue";
import EmptyState from "./EmptyState.vue";

export default {
  name: "DataTable",

  components: {
    StatusBadge,
    EmptyState,
  },

  props: {
    /**
     * Definición de columnas:
     * [
     *   { label: 'ID', field: 'id', width: '80', sortable: true, align: 'center' },
     *   { label: 'Nombre', field: 'name', sortable: true, align: 'left' },
     *   { label: 'Estado', field: 'activo', type: 'badge', width: '110' },
     *   { label: 'Fecha', field: 'created_at', formatter: (val) => val ? val.substring(0,10) : '' }
     * ]
     */
    columns: {
      type: Array,
      required: true,
      default: () => [],
    },

    /**
     * Lista de registros a mostrar
     */
    data: {
      type: Array,
      default: () => [],
    },

    /**
     * Campo único para identificar la fila (para key)
     */
    idKey: {
      type: [String, Function],
      default: "id",
    },

    /**
     * Campo por el cual está ordenada actualmente la tabla
     */
    orderByField: {
      type: String,
      default: "id",
    },

    /**
     * Dirección del ordenamiento: 'ASC' | 'DESC' | boolean
     */
    orderByDirection: {
      type: [String, Boolean],
      default: "DESC",
    },

    /**
     * Indica si se incluye la columna de acciones al final
     */
    hasActions: {
      type: Boolean,
      default: true,
    },

    /**
     * Título de la columna de acciones
     */
    actionsLabel: {
      type: String,
      default: "",
    },

    /**
     * Ancho de la columna de acciones
     */
    actionsWidth: {
      type: [String, Number],
      default: "110",
    },

    /**
     * Clases para la cabecera de acciones
     */
    actionsHeaderClass: {
      type: String,
      default: "text-center",
    },

    /**
     * Función opcional para aplicar clase a cada <tr>
     */
    rowClass: {
      type: Function,
      default: null,
    },

    /**
     * Mostrar automáticamente el EmptyState si no hay datos
     */
    showEmptyState: {
      type: Boolean,
      default: false,
    },

    /**
     * Mensaje para EmptyState
     */
    emptyMessage: {
      type: String,
      default: "No se encontraron registros que coincidan con la búsqueda.",
    },
  },

  methods: {
    getFieldValue(item, field) {
      if (!item || !field) return "";
      // Soporte para propiedades anidadas como 'usuario.nombre'
      if (typeof field === "string" && field.includes(".")) {
        return field.split(".").reduce((acc, part) => (acc ? acc[part] : ""), item);
      }
      const val = item[field];
      return val !== undefined && val !== null ? val : "";
    },

    getItemKey(item, index) {
      if (typeof this.idKey === "function") {
        return this.idKey(item, index);
      }
      if (item && this.idKey && item[this.idKey] !== undefined) {
        return item[this.idKey];
      }
      if (item) {
        const fallbackKey =
          item.id_producto ||
          item.id_servicio ||
          item.id_operador ||
          item.id_usuario ||
          item.id_keyword ||
          item.id_integrador ||
          item.id_billing_code ||
          item.id_billing_sequence ||
          item.id_atributo ||
          item.id_tarea;
        if (fallbackKey !== undefined) {
          return fallbackKey;
        }
      }
      return index;
    },

    orderClass(field) {
      if (!field || this.orderByField !== field) {
        return "";
      }
      if (typeof this.orderByDirection === "boolean") {
        return this.orderByDirection ? "asc" : "desc";
      }
      return String(this.orderByDirection).toUpperCase() === "ASC" ? "asc" : "desc";
    },

    onSort(field) {
      this.$emit("sort", field);
    },
  },
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
  user-select: none;
}
</style>
